import { store } from '@/store/store'
import { originalApi } from '@/store/api/originalApi'
import { paymentApi } from '@/store/api/paymentApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import {
	addBaccaratPendingChip,
	aggregateBaccaratChips,
	applyBaccaratRoundState,
	clearBaccaratLosingChips,
	clearBaccaratPendingChips,
	clearBaccaratTable,
	dismissBaccaratResult,
	scaleBaccaratPendingChips,
	selectBaccaratGame,
	setBaccaratClearing,
	setBaccaratSelectedChipIndex,
	setBaccaratSpotHighlighting,
	setBaccaratSubmitting,
	showBaccaratResult,
	undoBaccaratPendingChip,
} from '@/store/slices/baccaratSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { extractApiErrorMessage } from '@/lib/api-message'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import { BACCARAT_CHIP_REAL_VALUES } from '@/config/original'
import type { BaccaratBetPlace, BaccaratRoundState } from '@/types/baccarat'
import { SoundManager } from './SoundManager'

const CLEAR_STAGGER_MS = 200
const CLEAR_DURATION_MS = 480
const DEAL_STAGGER_MS = 140
const DEAL_DURATION_MS = 480
const RESULT_REVEAL_PAUSE_MS = 200
const RESULT_MODAL_MS = 3000
const SETTLE_HOLD_MS = 800
/** Spot highlight on/off cycles before settlement cleanup. */
const SPOT_FLASH_COUNT = 3
const SPOT_FLASH_MS = 500

function wait(ms: number) {
	return new Promise<void>((resolve) => {
		window.setTimeout(resolve, ms)
	})
}

function clearAnimationMs(cardCount: number) {
	if (cardCount <= 0) {
		return 0
	}

	return (cardCount - 1) * CLEAR_STAGGER_MS + CLEAR_DURATION_MS + 80
}

function dealAnimationMs(hands: number[]) {
	const longest = Math.max(0, ...hands)
	if (longest <= 0) {
		return 0
	}

	return (longest - 1) * DEAL_STAGGER_MS + DEAL_DURATION_MS + 80
}

export class GameController {
	private disposed = false

	dispose() {
		this.disposed = true
	}

	private get baccaratState() {
		return selectBaccaratGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string) {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private applyRoundState(state: BaccaratRoundState) {
		store.dispatch(applyBaccaratRoundState(state))
	}

	private hasLosingChips() {
		const { pendingChips, betResults } = this.baccaratState
		const losingPlaces = new Set(betResults.filter((result) => result.outcome === 'lost').map((result) => result.place))
		return pendingChips.some((chip) => losingPlaces.has(chip.place))
	}

	private async finishRoundAnimations(state: BaccaratRoundState, animationMs: number) {
		await wait(animationMs)
		if (this.disposed) {
			return
		}

		store.dispatch(setBaccaratSpotHighlighting(true))
		await wait(SPOT_FLASH_COUNT * 2 * SPOT_FLASH_MS)
		if (this.disposed) {
			return
		}

		if (this.hasLosingChips()) {
			SoundManager.instance.play('clear')
			store.dispatch(clearBaccaratLosingChips())
		}

		store.dispatch(setBaccaratSpotHighlighting(false))

		if (state.status === 'win' || state.profit > 0) {
			await wait(RESULT_REVEAL_PAUSE_MS)
			if (!this.disposed) {
				SoundManager.instance.play('win')
				store.dispatch(showBaccaratResult())
				await wait(RESULT_MODAL_MS)
				if (!this.disposed) {
					store.dispatch(dismissBaccaratResult())
				}
			}
		} else {
			await wait(SETTLE_HOLD_MS)
		}

		if (!this.disposed) {
			await this.clearTableIfNeeded()
		}
	}

	private async clearTableIfNeeded() {
		const { playerHand, bankerHand } = this.baccaratState
		const cardCount = Math.max(playerHand.length, bankerHand.length)
		if (cardCount === 0) {
			return
		}

		store.dispatch(setBaccaratClearing(true))
		SoundManager.instance.play('clear')
		await wait(clearAnimationMs(cardCount))

		if (this.disposed) {
			return
		}

		store.dispatch(clearBaccaratTable())
		store.dispatch(setBaccaratClearing(false))
		await wait(40)
	}

	selectChip(chipIndex: number) {
		if (!this.requireAuth('Sign in to select a chip.') || this.baccaratState.isSubmitting || this.baccaratState.isClearing) {
			return
		}
		store.dispatch(setBaccaratSelectedChipIndex(chipIndex))
	}

	placeBet(place: BaccaratBetPlace) {
		if (!this.requireAuth('Sign in to place chips on the table.') || this.baccaratState.isSubmitting || this.baccaratState.isClearing || this.baccaratState.phase !== 'ready') {
			return
		}

		const chipIndex = this.baccaratState.selectedChipIndex
		const amount = BACCARAT_CHIP_REAL_VALUES[chipIndex] ?? 0
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Select a valid chip.')
			return
		}

		store.dispatch(addBaccaratPendingChip({ place, chipIndex }))
		SoundManager.instance.play('bet')
	}

	undoBet() {
		if (!this.requireAuth('Sign in to edit bets.') || this.baccaratState.isSubmitting || this.baccaratState.isClearing) {
			return
		}
		store.dispatch(undoBaccaratPendingChip())
	}

	clearBets() {
		if (!this.requireAuth('Sign in to edit bets.') || this.baccaratState.isSubmitting || this.baccaratState.isClearing) {
			return
		}
		store.dispatch(clearBaccaratPendingChips())
		SoundManager.instance.play('clear')
	}

	scaleBets(factor: number) {
		if (!this.requireAuth('Sign in to edit bets.') || this.baccaratState.isSubmitting || this.baccaratState.isClearing || this.baccaratState.pendingChips.length === 0) {
			return
		}
		store.dispatch(scaleBaccaratPendingChips(factor))
	}

	async startGame() {
		if (this.baccaratState.isSubmitting || this.baccaratState.isClearing) {
			return
		}

		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const bets = aggregateBaccaratChips(this.baccaratState.pendingChips)
		if (!bets.length) {
			showApiErrorToast('Place at least one bet on the table.')
			return
		}

		store.dispatch(setBaccaratSubmitting(true))

		try {
			await this.clearTableIfNeeded()
			if (this.disposed) {
				return
			}

			const response = await store
				.dispatch(
					originalApi.endpoints.startBaccaratRound.initiate({
						currency_code: this.baccaratState.currencyCode,
						bets,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			if (this.disposed) {
				return
			}

			SoundManager.instance.play('deal')
			this.applyRoundState(response.data)
			await this.finishRoundAnimations(response.data, dealAnimationMs([response.data.playerHand.length, response.data.bankerHand.length]))

			if (!this.disposed) {
				store.dispatch(setBaccaratSubmitting(false))
			}
		} catch (error) {
			store.dispatch(setBaccaratClearing(false))
			store.dispatch(setBaccaratSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to start the baccarat round.')
		}
	}
}
